/**
 * src/components/agent/ChatHeader.styles.js — ChatHeader + ScopeChipRow + ScopeChipPicker.
 *
 * Phase 82-04 — Chat System Unification, header.
 * Tokens only (src/styles.js). No raw hex.
 */

export const rootStyle = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  background: 'var(--glass)',
  borderBottom: '1px solid var(--bdr)',
  flexShrink: 0,
};

export const topRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  minHeight: 56,
};

export const titleWrapStyle = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
};

export const titleTextStyle = {
  fontSize: 15,
  fontWeight: 600,
  color: 'var(--t1)',
  cursor: 'text',
  padding: '4px 8px',
  borderRadius: 6,
  outline: 'none',
  minWidth: 80,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontFamily: 'inherit',
};

export const titleEditingStyle = {
  ...titleTextStyle,
  background: 'var(--accent-bg)',
  border: '1px solid var(--accent)',
  padding: '3px 7px',
  whiteSpace: 'normal',
  overflow: 'visible',
};

export const iconBtnStyle = {
  minWidth: 44,
  minHeight: 44,
  width: 44,
  height: 44,
  border: 'none',
  background: 'transparent',
  color: 'var(--tm)',
  cursor: 'pointer',
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  transition: 'color 150ms, background 150ms',
};

export const iconBtnActiveStyle = {
  ...iconBtnStyle,
  color: 'var(--accent)',
  background: 'var(--accent-bg)',
};

/* ── ScopeChipRow ───────────────────────────────────────── */

export const chipRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '0 12px 8px',
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollbarWidth: 'thin',
  minHeight: 36,
};

export function chipStyle(type) {
  const palette = chipPalette(type);
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    minHeight: 28,
    padding: '3px 4px 3px 8px',
    borderRadius: 999,
    background: palette.bg,
    color: palette.fg,
    border: `1px solid ${palette.border}`,
    fontSize: 12,
    fontWeight: 500,
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  };
}

export const chipCloseBtnStyle = {
  minWidth: 24,
  minHeight: 24,
  width: 24,
  height: 24,
  border: 'none',
  background: 'transparent',
  color: 'inherit',
  cursor: 'pointer',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  opacity: 0.7,
};

export const addChipBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  minHeight: 44,
  padding: '8px 10px',
  borderRadius: 999,
  border: '1px dashed var(--bdr)',
  background: 'transparent',
  color: 'var(--tm)',
  fontSize: 12,
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
  flexShrink: 0,
};

/**
 * chipPalette — text-saturated fg, tint bg, subtle border.
 * Pairs text token with matching tint per HARD DEPLOYMENT RULES.
 */
export function chipPalette(type) {
  switch (type) {
    case 'project':
      return { fg: 'var(--accent)', bg: 'var(--accent-bg)', border: 'var(--bdr)' };
    case 'deal':
      return { fg: 'var(--navy)', bg: 'var(--accent-bg)', border: 'var(--bdr)' };
    case 'task':
      return { fg: 'var(--ok)', bg: 'var(--ok-tint)', border: 'var(--bdr)' };
    case 'person':
      return { fg: 'var(--warn)', bg: 'var(--warn-tint)', border: 'var(--bdr)' };
    default:
      return { fg: 'var(--t1)', bg: 'var(--stodob)', border: 'var(--bdr)' };
  }
}

/* ── ScopeChipPicker ────────────────────────────────────── */

export const pickerPopoverStyle = {
  position: 'absolute',
  top: '100%',
  right: 0,
  marginTop: 4,
  width: 320,
  maxWidth: '90vw',
  zIndex: 1200,
  background: 'var(--card, var(--bg))',
  border: '1px solid var(--bdr)',
  borderRadius: 10,
  boxShadow: 'var(--shc)',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 420,
};

export const pickerTabsStyle = {
  display: 'flex',
  borderBottom: '1px solid var(--bdr)',
  flexShrink: 0,
};

export function pickerTabStyle(active) {
  return {
    flex: 1,
    minHeight: 44,
    padding: '8px 4px',
    border: 'none',
    background: active ? 'var(--accent-bg)' : 'transparent',
    color: active ? 'var(--accent)' : 'var(--tm)',
    fontSize: 12,
    fontWeight: active ? 600 : 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
    textTransform: 'capitalize',
  };
}

export const pickerSearchStyle = {
  width: '100%',
  minHeight: 44,
  padding: '10px 12px',
  border: 'none',
  borderBottom: '1px solid var(--bdr)',
  background: 'var(--bg, var(--glass))',
  color: 'var(--t1)',
  fontSize: 13,
  fontFamily: 'inherit',
  outline: 'none',
};

export const pickerListStyle = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '4px 0',
};

export function pickerItemStyle(active) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    minHeight: 44,
    padding: '8px 12px',
    border: 'none',
    background: active ? 'var(--accent-bg)' : 'transparent',
    color: active ? 'var(--accent)' : 'var(--t1)',
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'left',
  };
}

export const pickerEmptyStyle = {
  padding: '16px 12px',
  color: 'var(--tm)',
  fontSize: 12,
  fontStyle: 'italic',
  textAlign: 'center',
};

/* TICKET-107 (a) — scope-filter note inside ScopeChipPicker's task tab. */
export const pickerScopeNoteStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '4px 10px',
  fontSize: 11,
  color: 'var(--tm)',
  borderBottom: '1px solid var(--bdr)',
};

export const pickerScopeNoteBtnStyle = {
  flexShrink: 0,
  minHeight: 44,
  padding: '8px 12px',
  border: 'none',
  background: 'transparent',
  color: 'var(--accent)',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
